import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  ExpandableSection,
  Modal,
  ModalVariant,
  Popover,
} from '@patternfly/react-core';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@patternfly/react-icons';
import Timestamp from 'react-timestamp';
import { Namespace, PreviewSessionOverlay, PreviewSessionWorkload } from '@app/Dashboard/models';
import { usePreviewStop, usePreviewStopAvailable } from '../PreviewEnv/hooks/usePreviewEnv';

const STOPPING_KEYS_STORAGE_KEY = 'ranch.previewSessionsCard.stoppingKeys';

function loadStoppingKeys(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STOPPING_KEYS_STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveStoppingKeys(keys: Set<string>): void {
  try {
    sessionStorage.setItem(STOPPING_KEYS_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // sessionStorage unavailable (private mode, etc.) — stopping state just won't persist across navigation
  }
}

interface PreviewSessionsCardProps {
  namespace: Namespace;
}

// Earliest startedAt / latest expiresAt across a session's workloads — they're not
// guaranteed to be byte-identical, so compute defensively rather than assume.
function sessionTimeRange(session: PreviewSessionOverlay): { startedAt?: Date; expiresAt?: Date } {
  const started = session.workloads.map(w => w.startedAt).filter(Boolean).map(d => new Date(d as string));
  const expires = session.workloads.map(w => w.expiresAt).filter(Boolean).map(d => new Date(d as string));
  return {
    startedAt: started.length ? new Date(Math.min(...started.map(d => d.getTime()))) : undefined,
    expiresAt: expires.length ? new Date(Math.max(...expires.map(d => d.getTime()))) : undefined,
  };
}

const WorkloadStatus: React.FC<{ workload: PreviewSessionWorkload }> = ({ workload }) => {
  const ready = workload.readyStatus === 'True';
  if (ready) {
    return (
      <span style={{ color: '#3e8635', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <CheckCircleIcon /> Ready
      </span>
    );
  }
  return (
    <Popover
      triggerAction="hover"
      bodyContent={workload.readyMessage || workload.readyReason || 'Not ready'}
    >
      <span style={{ color: '#c58c00', cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <ExclamationTriangleIcon /> {workload.readyReason || 'Not ready'}
      </span>
    </Popover>
  );
};

export const PreviewSessionsCard: React.FC<PreviewSessionsCardProps> = ({ namespace }) => {
  const { stopPreview, isStopping, stopError } = usePreviewStop();
  const stopAvailable = usePreviewStopAvailable();
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  // Persisted (not just local state) so a stop that's been accepted by Jenkins but not yet torn
  // down survives navigating away and back, and the scraper's 10-15 min lag can't make a second
  // click re-trigger ACTION=stop on the same session. Cleared below once the scrape confirms the
  // session is actually gone — never cleared on a timer, since teardown time isn't fixed.
  const [stoppingKeys, setStoppingKeys] = useState<Set<string>>(() => loadStoppingKeys());

  const sessions = namespace.previewSessionOverlays || [];

  useEffect(() => {
    const liveKeys = new Set(sessions.map(s => s.key));
    setStoppingKeys(prev => {
      const next = new Set([...prev].filter(k => liveKeys.has(k)));
      if (next.size === prev.size) return prev;
      saveStoppingKeys(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace.previewSessionOverlays]);

  if (sessions.length === 0 && stoppingKeys.size === 0) return null;

  const confirmSession = sessions.find(s => s.key === confirmKey) ?? null;

  const handleConfirmStop = async () => {
    if (!confirmSession) return;
    const ok = await stopPreview(namespace.name, confirmSession.key);
    if (ok) {
      setStoppingKeys(prev => {
        const next = new Set(prev).add(confirmSession.key);
        saveStoppingKeys(next);
        return next;
      });
      setConfirmKey(null);
    }
  };

  return (
    <div className="dx-card">
      <div className="dx-card__title">Preview Sessions</div>
      <div className="dx-card__body">
        <div className="dx-stack dx-stack--gutter">
          {stoppingKeys.size > 0 && (
            <Alert
              variant="info"
              isInline
              title="Stop requested — the cluster view may take a few minutes to update."
            />
          )}
          {sessions.map(session => {
            const allReady = session.workloads.every(w => w.readyStatus === 'True');
            const { startedAt, expiresAt } = sessionTimeRange(session);
            const isExpanded = !!expandedKeys[session.key];
            const isStoppingThis = stoppingKeys.has(session.key);

            return (
              <div key={session.key} style={{ borderBottom: '1px solid #d2d2d2', paddingBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: allReady ? '#3e8635' : '#f0ab00',
                    }}
                  />
                  <strong style={{ flexGrow: 1 }}>{session.key}</strong>
                  {isStoppingThis ? (
                    <span style={{ color: '#6a6e73', fontSize: '0.85rem' }}>Stopping…</span>
                  ) : stopAvailable && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setConfirmKey(session.key)}
                    >
                      Stop
                    </Button>
                  )}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6a6e73', margin: '4px 0' }}>
                  {session.workloads.length} workload{session.workloads.length === 1 ? '' : 's'}
                  {startedAt && <> · started <Timestamp relative date={startedAt} options={{ includeDay: false }} /></>}
                  {expiresAt && <> · expires <Timestamp relative date={expiresAt} options={{ includeDay: false }} /></>}
                </div>
                <ExpandableSection
                  toggleText={isExpanded ? 'Hide workloads' : 'Show workloads'}
                  isExpanded={isExpanded}
                  onToggle={(_e, expanded) => setExpandedKeys(prev => ({ ...prev, [session.key]: expanded }))}
                >
                  <div className="dx-stack">
                    {session.workloads.map(workload => (
                      <div
                        key={workload.name}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}
                      >
                        <span style={{ minWidth: '120px' }}>{workload.targetName}</span>
                        <WorkloadStatus workload={workload} />
                        <span style={{ color: '#6a6e73', fontSize: '0.85rem' }} title={workload.image}>
                          {workload.image}
                        </span>
                      </div>
                    ))}
                  </div>
                </ExpandableSection>
              </div>
            );
          })}
        </div>
      </div>

      {confirmSession && (
        <Modal
          variant={ModalVariant.small}
          title="Stop preview session?"
          isOpen
          onClose={() => setConfirmKey(null)}
          actions={[
            <Button
              key="stop"
              variant="danger"
              isLoading={isStopping}
              isDisabled={isStopping}
              onClick={handleConfirmStop}
            >
              Stop session
            </Button>,
            <Button key="cancel" variant="link" onClick={() => setConfirmKey(null)} isDisabled={isStopping}>
              Cancel
            </Button>,
          ]}
        >
          This will tear down <strong>{confirmSession.key}</strong> and{' '}
          {confirmSession.workloads.length === 1 ? 'its workload' : `all ${confirmSession.workloads.length} of its workloads`}
          {' '}({confirmSession.workloads.map(w => w.targetName).join(', ')}) in{' '}
          <strong>{namespace.name}</strong>. This cannot be undone.
          {stopError && <Alert variant="danger" isInline title={stopError} style={{ marginTop: '1rem' }} />}
        </Modal>
      )}
    </div>
  );
};
